<script setup>

import {ErrorMessage, Field, Form} from 'vee-validate'
import {computed, reactive, ref} from 'vue'
import axios from 'axios'
import {useAddressField} from '../../composables/useAddressField'
import GoogleAddress from './../Google/GoogleAddress.vue'
import {useFormSubmit} from '../../composables/useFormSubmit'

const { showServerSideValidateErrors, enquirySubmitDataLayer, sendFBLeadData } = useFormSubmit()
const {showAddressFields, addressValueChange, showCustomAddressLink, toggleCustomAddress} = useAddressField()

const validationSchema = computed(() => {
  let schema = {
    first_name: 'required',
    last_name: 'required',
    email: 'required|email',
    phone_number: 'required|phoneNumber',
  }

  if (showAddressFields.value) {
    schema.street_address = 'required'
    schema.postcode = 'required|numeric'
    schema.city = 'required'
  }

  return schema
})

const confirmation = reactive({show: false, message: '', class: ''})
let isSubmitting = ref(false)
const subjectRadio = ref('Request an information pack')
const googleAddressRef = ref(null)

const onSubmit = async () => {
  isSubmitting.value = true
  confirmation.show = true
  confirmation.class = 'message--warning'
  confirmation.message = 'Submitting....'
  const form  = document.getElementById('villageContactForm');
  let formData = new FormData(form);
  window.grecaptcha.execute(document.getElementById('mySiteKeyVariable').value, {
    action: formData.get('captcha_action'),
  }).then((token) => {
    const submitButton = form.querySelector('.submit');
    submitButton.disabled = true;
    formData.set('g-recaptcha-response', token);
    axios.post('/enquiry-submitted', formData).then(function (response) {
      confirmation.class = 'message--success'
      confirmation.message = 'Your message was successfully sent';
      enquirySubmitDataLayer(form);
      sendFBLeadData(formData.get('subject'));
      form.reset();
      googleAddressRef.value.resetFields();
      setTimeout(function () {
        confirmation.show = false;
      }, 4000);
    }).catch(error => {
      submitButton.disabled = false;
      showServerSideValidateErrors(error, isSubmitting, confirmation)
    });
  });
}
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    v-slot="{ errors }"
    @submit="onSubmit"
    id="villageContactForm"
  >
    <slot name="token"></slot>
    <div class="village__form">
      <div class="row">
        <div class="col-lg-12 pt-4 pb-md-6">
          <label class="d-inline-block mr-2 mb-5 mb-md-0"><strong>I would like to:</strong></label>
          <div class="radio-custom radio-custom--inline radio-custom--white">
            <input v-model="subjectRadio" type="radio" id="request-information-pack" name="subject" value="Request an information pack">
            <label class="form-check-label" for="request-information-pack">Request an information pack</label>
          </div>
          <div class="radio-custom radio-custom--inline radio-custom--white">
            <input v-model="subjectRadio" type="radio" id="ask-question" name="subject" value="General enquiry">
            <label class="form-check-label" for="ask-question">Ask a question</label>
          </div>
        </div>
      </div>
      <div class="row mt-4 mt-md-8">
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="first_name">First name <span class="text-red">*</span></label>
            <Field
              id="first_name"
              name="first_name"
              class="form-control"
              :class="{'is-danger': errors['first_name'] }"
            />
            <ErrorMessage name="first_name" class="is-danger" />

          </div>
        </div>
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="last_name">Last name <span class="text-red">*</span></label>
            <Field
              name="last_name"
              class="form-control"
              :class="{'is-danger': errors['last_name'] }"
              id="last_name"
            />
            <ErrorMessage name="last_name" class="is-danger" />
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="email">Email <span class="text-red">*</span></label>
            <Field
              id="email"
              name="email"
              class="form-control"
              :class="{'is-danger': errors['email'] }"
            />
            <ErrorMessage name="email" class="is-danger" />
          </div>
        </div>
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="phone_number">Phone number <span class="text-red">*</span></label>
            <Field
              id="phone_number"
              name="phone_number"
              class="form-control"
              :class="{'is-danger': errors['phone_number'] }"
            />
            <ErrorMessage name="phone_number" class="is-danger" />
          </div>
        </div>
      </div>
      <div class="row mt-4" v-if="showAddressFields">
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="street_address">Street address <span class="text-red">*</span></label>
            <Field
              id="street_address"
              name="street_address"
              class="form-control"
              :class="{'is-danger': errors['street_address'] }"
            />
            <ErrorMessage name="street_address" class="is-danger" />
          </div>
        </div>
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="city">City <span class="text-red">*</span></label>
            <Field
              id="city"
              name="city"
              class="form-control"
              :class="{'is-danger': errors['city'] }"
            />
            <ErrorMessage name="city" class="is-danger" />
          </div>
        </div>
      </div>
      <div class="row mt-4" v-if="showAddressFields">
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="postcode">Post code <span class="text-red">*</span></label>
            <Field
              id="postcode"
              name="postcode"
              class="form-control"
              :class="{'is-danger': errors['postcode'] }"
            />
            <ErrorMessage name="postcode" class="is-danger" />
          </div>
        </div>
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <slot name="country"></slot>
          </div>
        </div>
      </div>
      <div class="mt-4">
        <div class="form-group form-group--input" v-if="!showAddressFields">
          <label for="address">Address</label>
          <google-address
            ref="googleAddressRef"
            :address-id="'village-page-contact-enquiry'"
            v-on:addressvaluechange="addressValueChange"
            :classname="'form-control form-control--bg-white ph-16'"
          ></google-address>
          <div v-if="showCustomAddressLink" class="mt-2 fs-14 font-weight-600" >
            Can't find your address? <a href="#" @click.prevent="toggleCustomAddress" id="enterCustomAddress">Enter it here</a>
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-lg-12">
          <div class="form-group form-group--input">
            <label for="comment">Message</label>
            <Field
              as="textarea"
              id="message"
              rows="6"
              class="form-control"
              name="message"
            ></Field>
          </div>
        </div>
      </div>
      <div class="checkbox-custom mt-4 mb-4">
        <input type="checkbox" class="subscribe" id="subscribe" name="subscribe" value="1" checked>
        <label class="form-check-label" for="subscribe">I would like to receive emails with village info, updates and more from Village Guide</label>
      </div>
      <input type="hidden" name="captcha_action" value="village_enquiry">
      <transition name="fade">
        <div :class="confirmation.class" class="message" v-if="confirmation.show">{{ confirmation.message }}</div>
      </transition>

      <div class="row">
        <div class="col-lg-12 text-center">
          <input type="submit" value="Send Message" class="button button--green button--h-45 fs-14 pl-15 pr-15 w-auto submit mt-6 mb-4 ml-auto mr-auto">
        </div>
      </div>
    </div>
  </Form>
</template>
